"use client";

import { useState, useEffect, useMemo } from "react";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { API_BASE_URL } from "@/lib/ApiFetch";

// Global cache to track which URLs have successfully loaded
// This prevents the loading skeleton from showing again when navigating back/forth
const loadedImagesCache = new Set<string>();

/** Service IDs with a custom avatar at public/service_img/{serviceId}.png */
const CUSTOM_SERVICE_AVATAR_IDS = new Set([
  "cmjcrj5ad000gg8xz8icco4jr",
  "cmjcrj5ad000gg8xz8icco4jq",
]);

function getCustomServiceAvatarUrl(serviceId: string): string | undefined {
  if (!CUSTOM_SERVICE_AVATAR_IDS.has(serviceId)) return undefined;
  return `/service_img/${serviceId}.png`;
}

interface WorkerAvatarProps {
  serviceId?: string;
  name?: string;
  thumbnail?: string;
  size?: number | string; // Allow number (px) or string (tailwind classes)
  className?: string; // For additional classes like rounded-lg, rounded-full
  imageColor?: string; // Fallback background color if everything fails
  showInitials?: boolean; // Whether to fallback to initials (default true)
  onImageLoad?: () => void; // Callback when image is ready
  skipLoading?: boolean; // If true, skips loading state and assumes image is ready (useful for transitions)
}

export const WorkerAvatar = ({
  serviceId,
  name,
  thumbnail,
  size = 40,
  className = "rounded-full",
  imageColor = "bg-aera-lightgray",
  showInitials = true,
  onImageLoad,
  skipLoading = false,
}: WorkerAvatarProps) => {
  const resolvedServiceId = serviceId?.trim() ?? "";

  const customServiceAvatarUrl = useMemo(
    () => getCustomServiceAvatarUrl(resolvedServiceId),
    [resolvedServiceId],
  );

  // Avatar URL (DiceBear - Micah style as fallback), seeded by serviceId only
  const avatarUrl = useMemo(() => {
    const params = new URLSearchParams({
      seed: resolvedServiceId,
      mouth: "smile,laughing,smirk",
      eyes: "smiling,smilingShadow",
    });
    return `https://api.dicebear.com/9.x/micah/svg?${params.toString()}`;
  }, [resolvedServiceId]);

  // Handle relative URLs by prepending API_BASE_URL
  const normalizedThumbnail = useMemo(() => {
    if (!thumbnail) return undefined;
    if (thumbnail.startsWith("http")) return thumbnail;

    const baseUrl = API_BASE_URL.endsWith("/")
      ? API_BASE_URL.slice(0, -1)
      : API_BASE_URL;
    return `${baseUrl}${thumbnail.startsWith("/") ? thumbnail : `/${thumbnail}`}`;
  }, [thumbnail]);

  // Check if current image is already cached
  const isCached = useMemo(() => {
    if (customServiceAvatarUrl && loadedImagesCache.has(customServiceAvatarUrl))
      return true;
    if (thumbnail && loadedImagesCache.has(thumbnail)) return true;
    if (
      !customServiceAvatarUrl &&
      !thumbnail &&
      loadedImagesCache.has(avatarUrl)
    )
      return true;
    return false;
  }, [customServiceAvatarUrl, thumbnail, avatarUrl]);

  // State to track load errors
  const [customAvatarError, setCustomAvatarError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  // State to track if the CURRENT image being shown is loaded
  // Initialize with TRUE if already cached OR skipLoading is true
  const [isLoaded, setIsLoaded] = useState(isCached || skipLoading);

  // Reset states when thumbnail or serviceId changes
  useEffect(() => {
    setCustomAvatarError(false);
    setThumbnailError(false);
    setAvatarError(false);

    // Check cache again for new props
    const nextIsCached =
      (customServiceAvatarUrl &&
        loadedImagesCache.has(customServiceAvatarUrl)) ||
      (thumbnail && loadedImagesCache.has(thumbnail)) ||
      (!customServiceAvatarUrl &&
        !thumbnail &&
        loadedImagesCache.has(avatarUrl));

    setIsLoaded(!!nextIsCached || skipLoading);
  }, [
    customServiceAvatarUrl,
    thumbnail,
    resolvedServiceId,
    avatarUrl,
    skipLoading,
  ]);

  // Safety timeout: If image doesn't load within 2 seconds, force show it
  useEffect(() => {
    if (!isLoaded) {
      const timer = setTimeout(() => {
        setIsLoaded(true);
        onImageLoad?.();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, onImageLoad]);

  // Determine container styles based on size type
  const sizeStyle =
    typeof size === "number" ? { width: size, height: size } : {};
  const sizeClass = typeof size === "string" ? size : "";

  const handleLoad = (src: string) => {
    loadedImagesCache.add(src);
    setIsLoaded(true);
    onImageLoad?.();
  };

  const showCustomAvatar = customServiceAvatarUrl && !customAvatarError;
  const showThumbnail = !showCustomAvatar && thumbnail && !thumbnailError;
  // Only show DiceBear if custom + thumbnail are missing/failed
  const showAvatar = !showCustomAvatar && !showThumbnail && !avatarError;
  // Show initials if all image sources failed and enabled
  const showInitialsFallback =
    !showCustomAvatar && !showThumbnail && !showAvatar && showInitials;

  return (
    <div
      className={`relative overflow-hidden flex items-center justify-center rounded-full text-sm font-bold shrink-0 ${className} ${sizeClass} ${
        !isLoaded && !showInitialsFallback
          ? "bg-aera-lightgray"
          : showInitialsFallback
            ? imageColor
            : "bg-aera-lightgray"
      } `}
      style={sizeStyle}
    >
      {/* Loading Skeleton - Only show if NOT loaded and NOT showing final initials fallback */}
      {!isLoaded && !showInitialsFallback && (
        <div className="absolute inset-0 bg-aera-lightgray animate-pulse z-10" />
      )}

      {showCustomAvatar ? (
        <AeraImage
          native
          key={customServiceAvatarUrl}
          src={customServiceAvatarUrl!}
          alt={name || serviceId || ""}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => handleLoad(customServiceAvatarUrl)}
          onError={() => {
            setCustomAvatarError(true);
            setIsLoaded(false);
          }}
        />
      ) : showThumbnail ? (
        <AeraImage
          native
          key={normalizedThumbnail}
          src={normalizedThumbnail!}
          alt={name || serviceId || ""}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => handleLoad(normalizedThumbnail!)}
          onError={() => {
            setThumbnailError(true);
            setIsLoaded(false); // Reset to loading to show skeleton while avatar loads
          }}
        />
      ) : showAvatar ? (
        <AeraImage
          native
          key={avatarUrl}
          src={avatarUrl}
          alt={name || serviceId || ""}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => handleLoad(avatarUrl)}
          onError={() => {
            setAvatarError(true);
            setIsLoaded(true); // Stop loading logic, reveal initials
            onImageLoad?.();
          }}
        />
      ) : (
        showInitialsFallback && (
          <span className="text-aera-white drop-shadow-md select-none animate-in fade-in zoom-in duration-300">
            {name ? name[0].toUpperCase() : "?"}
          </span>
        )
      )}
    </div>
  );
};

export default WorkerAvatar;
